import React, { useCallback, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Box,
  FlatTextInput,
  Form,
  getValidationResolver,
  Heading,
  onKeyDownForCancellingAutoComplete,
  Preloader,
  Text,
  useIsWindowsOS,
  useResponsive,
} from '@common-fe/common-fe';
import _ from 'lodash';
import * as yup from 'yup';

import { VALIDATORS } from '@/common';
import { AUTH_ERROR_KEY, REQUIRED_TEXT } from '@/common/constants';
import { DefaultValues } from '@/common/types';
import AppButton from '@/components/controls/AppButton';

import { useForgotPassword } from './hooks';

const CODE_ENTITY_NAME = 'verificationCode';
const NEW_PASSWORD_FIELD_KEY = 'newPassword';
const CONFIRM_PASSWORD_FIELD_KEY = 'confirmPassword';

interface Props {
  username: string;
  testId?: string;
  children?: React.ReactNode;
  onNext: (value: object) => void;
}

const NewPassword: React.FC<Props> = ({ username, onNext }) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const { PASSWORD_MUST_MATCH } = AUTH_ERROR_KEY;
  const {
    errorText,
    handleResetError,
    handleSetNewCode,
    handleRequestCodeWithTimeout,
    disabled: disabledResend,
    isResetLoading,
  } = useForgotPassword();

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm({
    defaultValues: {},
    resolver: getValidationResolver({
      [CODE_ENTITY_NAME]: yup.string().required(REQUIRED_TEXT),
      [NEW_PASSWORD_FIELD_KEY]: VALIDATORS.PASSWORD,
      [CONFIRM_PASSWORD_FIELD_KEY]: yup
        .string()
        .required(REQUIRED_TEXT)
        .oneOf([yup.ref(NEW_PASSWORD_FIELD_KEY)], PASSWORD_MUST_MATCH),
    }),
  });

  const onSubmit = useCallback(
    async (data: DefaultValues) => {
      const code = _.get(data, CODE_ENTITY_NAME, '') as string;

      const password = _.get(data, NEW_PASSWORD_FIELD_KEY, '') as string;

      await handleSetNewCode(username, code, password, onNext);
    },
    [handleSetNewCode, onNext, username]
  );
  const isWindowsOS = useIsWindowsOS();
  const [isCodeAvailable, setIsCodeAvailable] = useState(!isWindowsOS);
  const [isNewPassAvailable, setIsNewPassAvailable] = useState(!isWindowsOS);

  return (
    <Box>
      <Form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
        <Box direction="column">
          <Box
            round="container1Round"
            pad={{
              horizontal: isMobile ? 'spacing12' : 'spacing48',
            }}
          >
            <Heading level={3} size="medium" weight={700} color="textTitle" textAlign="center">
              {t('Enter verification code')}
            </Heading>
            <Text textAlign="center" size="large" weight={400} margin={{ top: 'spacing4' }}>
              {t('Please enter the code you received in email to verify your account.')}
            </Text>
            <Controller
              // @ts-ignore
              name={CODE_ENTITY_NAME}
              control={control}
              render={({ field: { value, onChange } }) => (
                <Box margin={{ top: 'spacing24' }}>
                  <FlatTextInput
                    {...(isWindowsOS && !isCodeAvailable
                      ? {
                          value: isCodeAvailable ? value : '',
                          onKeyDown: (e) =>
                            onKeyDownForCancellingAutoComplete(
                              e,
                              onChange,
                              setIsCodeAvailable,
                              isCodeAvailable
                            ),
                        }
                      : {
                          value,
                        })}
                    onChange={(newValue: string) => {
                      onChange(newValue);
                      handleResetError();
                    }}
                    testId="input-verification-code"
                    name={CODE_ENTITY_NAME}
                    defaultAutoComplete="off"
                    errorWrap
                    isLarge
                    placeholder={t('Enter verification code')}
                    errorText={
                      errorText ? errorText : (_.get(errors, `${CODE_ENTITY_NAME}.message`, '') as string)
                    }
                  />
                </Box>
              )}
            />
            <Box direction="row" margin={{ top: 'spacing12' }}>
              <AppButton
                testId="resend-code"
                buttonType="quaternary"
                disabled={disabledResend}
                style={{ padding: 0, height: '24px', minHeight: '24px' }}
                onClick={() => handleRequestCodeWithTimeout(username)}
              >
                Resend Code
              </AppButton>
            </Box>
          </Box>
          <Box
            margin={{ vertical: 'spacing24', horizontal: isMobile ? 'spacing12' : 'spacing48' }}
            height="1px"
            background="border1"
          />
          <Box
            round="container1Round"
            pad={{
              horizontal: isMobile ? 'spacing12' : 'spacing48',
            }}
          >
            <Heading level={3} size="medium" color="textTitle" weight={700} textAlign="center">
              {t('Reset Password')}
            </Heading>

            <Text textAlign="center" size="large" weight={400} margin={{ top: 'spacing4' }}>
              {t('Please choose your new password.')}
            </Text>
            <Controller
              // @ts-ignore
              name={NEW_PASSWORD_FIELD_KEY}
              control={control}
              render={({ field: { value, onChange } }) => (
                <Box
                  margin={{ top: 'l', bottom: _.get(errors, `${NEW_PASSWORD_FIELD_KEY}.message`) ? 'm' : '' }}
                  id="password-input-id"
                >
                  <FlatTextInput
                    {...(isWindowsOS && !isNewPassAvailable
                      ? {
                          value: isNewPassAvailable ? value : '',
                          onKeyDown: (e) =>
                            onKeyDownForCancellingAutoComplete(
                              e,
                              onChange,
                              setIsNewPassAvailable,
                              isNewPassAvailable
                            ),
                        }
                      : {
                          value,
                        })}
                    onChange={(newValue: string) => {
                      onChange(newValue);
                      handleResetError();
                    }}
                    testId="password-input"
                    passwordHelper
                    {...isMobile ? { passwordHelperBottomPosition: true } : { passwordHelperLeftPosition: true }}
                    defaultAutoComplete="off"
                    inputType="password"
                    errorWrap
                    isLarge
                    name={NEW_PASSWORD_FIELD_KEY}
                    placeholder={t('Choose a new password')}
                    errorText={_.get(errors, `${NEW_PASSWORD_FIELD_KEY}.message`, '') as string}
                  />
                </Box>
              )}
            />

            <Controller
              // @ts-ignore
              name={CONFIRM_PASSWORD_FIELD_KEY}
              control={control}
              render={({ field: { value, onChange } }) => (
                <Box margin={{ top: 'spacing12' }}>
                  <FlatTextInput
                    inputType="password"
                    value={value}
                    isLarge
                    errorWrap
                    onChange={(newValue: string) => {
                      onChange(newValue);
                      handleResetError();
                    }}
                    testId="confirm-password-input"
                    name={CONFIRM_PASSWORD_FIELD_KEY}
                    placeholder={t('Confirm new password')}
                    errorText={_.get(errors, `${CONFIRM_PASSWORD_FIELD_KEY}.message`, '') as string}
                  />
                </Box>
              )}
            />
          </Box>
          <Box
            margin={{ top: isMobile ? 'spacing24' : undefined }}
            pad={{
              top: 'spacing12',
              horizontal: isMobile ? 'spacing12' : 'spacing48',
            }}
          >
            <AppButton size="L" testId="submit" type="submit" width="100%">
              {isResetLoading ? <Preloader /> : t('Submit')}
            </AppButton>
          </Box>
        </Box>
      </Form>
    </Box>
  );
};

export default NewPassword;
